// BillingReturnHandler — headless component that reacts to the URL
// parameters Stripe Checkout sends us back with.
//
// Stripe's `success_url` is `/app?billing=success&plan=<plan>&session_id=...`
// and `cancel_url` is `/app?billing=cancel`. On either, we want to:
//   1. Show the user a toast so they know what happened.
//   2. On success, force-refresh the useSubscription hook so the UI flips
//      without waiting for the (possibly-delayed) realtime push from the
//      webhook.
//   3. Strip the billing params out of the URL so a refresh/back-button
//      doesn't replay the toast + refresh.
//
// This is mounted once, high up in the /app tree (inside TattooApp). It
// returns null — it only has side effects.
//
// We intentionally don't block rendering on the refresh: the toast fires
// immediately, and the realtime channel will correct the entitlement within
// a second or two anyway. refresh() is belt-and-suspenders for cases where
// the webhook lands fractionally after the redirect.
//
// Plan hint:
//   We pass the plan name as a URL param (?plan=customer_pro) so the toast
//   can say "Welcome to Customer Pro" without having to query Stripe.
//   If it's missing (older checkout session), we fall back to a generic
//   message.
//
// Single-design (one-off) note:
//   The one-off purchase also returns via ?billing=success, but grants a
//   design_credit rather than a subscription. The toast copy below is
//   written to work for both cases.

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useSubscription } from "../hooks/useSubscription";
import { PLANS } from "./constants";

export default function BillingReturnHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useSubscription();

  // Guard against the effect re-running (e.g. strict-mode double-invoke or
  // a location re-render) after we've already handled this return.
  const handledKeyRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const billing = params.get("billing");
    if (!billing) return;

    // Build a stable key so we only react once per unique return.
    const key = `${billing}:${params.get("session_id") || ""}:${params.get("plan") || ""}`;
    if (handledKeyRef.current === key) return;
    handledKeyRef.current = key;

    if (billing === "success") {
      const planId = params.get("plan");
      const plan = planId && PLANS[planId];

      let successMsg;
      if (!plan) {
        successMsg = "Payment confirmed — thanks!";
      } else if (plan.id === "single_design") {
        successMsg = "Design credit added — have at it.";
      } else {
        successMsg = `Welcome to ${plan.name} — you're all set.`;
      }
      toast.success(successMsg, { duration: 5000 });

      // Force-refresh the subscription hook so the UI picks up the new
      // entitlement immediately without waiting on realtime.
      if (typeof refresh === "function") {
        refresh();
        // Also retry once after a short delay in case the webhook is still
        // processing when the user lands back. Two pulls covers almost every
        // real-world delay without hammering the DB.
        const t = setTimeout(() => refresh(), 1500);
        return () => clearTimeout(t);
      }
    } else if (billing === "cancel") {
      toast("Checkout canceled — no charge was made.", {
        icon: "↩️",
        duration: 3500,
      });
    }
  }, [location.search, refresh]);

  // Strip billing params from the URL after we handle them so a reload or
  // back-button doesn't retrigger the toast. Runs in its own effect so it
  // doesn't block the toast from showing first.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("billing")) return;

    params.delete("billing");
    params.delete("session_id");
    params.delete("plan");
    const cleanSearch = params.toString();

    navigate(
      {
        pathname: location.pathname,
        search: cleanSearch ? `?${cleanSearch}` : "",
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}
